
import { Dispatch, SetStateAction, useRef, useState } from "react";
import Styles from "./ItemImageUploadComp.module.css";
import { ItemImage, ItemImageFile, ItemModel } from "../models/ItemModel";
import InfoDialogueComp from "./InfoDialogueComp";
import { ItemValidatonErrors } from "../reducers/itemFormReducer";

interface ItemImageUploadCompProps {
    item: ItemModel;
    itemImages: (ItemImageFile | ItemImage)[]; // Array of File objects or image URLs,
    itemImagesToDelete?: { itemId: number, imageName: string }[]; // Optional array for images to be deleted
    newItemErrors?: ItemValidatonErrors; // Object to hold error messages for form fields
    onServiceErrorChange?: (error: string) => void; // Function to set service error message
    onItemChange?: (item: ItemModel) => void; // Function to update the item
    onItemImagesChange?: Dispatch<SetStateAction<(ItemImageFile | ItemImage)[]>>;
    onItemImagesToDeleteChange?: Dispatch<SetStateAction<{ itemId: number, imageName: string }[]>>; // Optional function to update images to delete
    viewOnly?: boolean; // Optional prop to indicate if the component is in view-only mode
}


const ItemImageUploadComp: React.FC<ItemImageUploadCompProps> = (
    { item, itemImages, itemImagesToDelete, newItemErrors, onServiceErrorChange, onItemChange, onItemImagesChange, onItemImagesToDeleteChange, viewOnly = false } // Destructure props
) => {

    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxImageSizeMB = 20;
    const maxNoOfImages = 20; // Maximum number of images allowed
    const [infoModalVisible, setInfoModalVisible] = useState(false);
    const [infoModalMessage, setInfoModalMessage] = useState('');

    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    // Handles the hover state for the image thumbnail
    // This state is used to show the "Set Primary" button when hovering over an image thumbnail
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);


    // Use refs to keep track of the drag and drop indices
    // These are used to handle the drag-and-drop reordering of images
    const dragItemIndex = useRef<number | null>(null);
    const dragOverItemIndex = useRef<number | null>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const selectedFiles = Array.from(files).slice(0, maxNoOfImages - itemImages.length);
            const validFiles = getValidPhotos(selectedFiles);
            if (onItemImagesChange) {
                onItemImagesChange(prev => [...prev, ...validFiles]);
            }
        }
    };
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    // Handles drag-and-drop to upload images
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = getValidPhotos(droppedFiles);
        onItemImagesChange && onItemImagesChange(prev => [...prev, ...validFiles]);
    };

    // Handles drag-and-drop to reorder images
    const handleDropImageItem = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (
            dragItemIndex.current !== null &&
            dragOverItemIndex.current !== null &&
            dragItemIndex.current !== dragOverItemIndex.current
        ) {
            const newImages = [...itemImages];
            const draggedItem = newImages.splice(dragItemIndex.current, 1)[0];
            newImages.splice(dragOverItemIndex.current, 0, draggedItem);

            // Update sortOrder based on new position 
            newImages.forEach((img, i) => {
                img.sortOrder = i + 1; // or zero-based: i
            });

            onItemImagesChange && onItemImagesChange(newImages);
        }

        dragItemIndex.current = null;
        dragOverItemIndex.current = null;
    };

    const handleRemoveImage = (index: number) => {
        const image = itemImages[index];
        if (image === undefined) return; // Check if the image exists

        if (image instanceof ItemImageFile) {
            onItemImagesChange && onItemImagesChange(itemImages.filter((_, i) => i !== index));
            if (item.primaryImage === image.file.name) {
                // If the removed image is the primary image, reset it  
                onItemChange && onItemChange({ ...item, primaryImage: "" });
            }
        }
        else if (image instanceof ItemImage) {
            const imageName = image.fileName;
            if (item.primaryImage === imageName) {
                onItemChange && onItemChange({ ...item, primaryImage: "" });
            }
            onItemImagesChange && onItemImagesChange(itemImages.filter((_, i) => i !== index));
            if (onItemImagesToDeleteChange) {
                onItemImagesToDeleteChange(prev => [...prev, { itemId: item.itemId, imageName }]);
            }


        }
    };



    const handleSetPrimaryImage = (img: ItemImageFile | ItemImage) => {
        if (img instanceof ItemImageFile) {
            // Subclass first
            onItemChange?.({ ...item, primaryImage: img.file.name });
        } else if (img instanceof ItemImage) {
            onItemChange?.({ ...item, primaryImage: img.fileName });
        }
    }

    const getValidPhotos = (files: File[]) => {

        const maxSizeBytes = maxImageSizeMB * 1024 * 1024;

        const validFiles: File[] = [];
        const selectedFiles = files.slice(0, maxNoOfImages - itemImages.length);
        if (files.length > selectedFiles.length) {
            setInfoModalMessage(`You have exceeded ${maxNoOfImages} images in total.`);
            setInfoModalVisible(true);
        }

        selectedFiles.forEach((file, index) => {
            if (allowedImageTypes.includes(file.type) && file.size <= maxSizeBytes) {
                validFiles.push(file);
            }
        });

        if (validFiles.length < selectedFiles.length) {
            setInfoModalMessage(`Photos should be less than ${maxImageSizeMB} MB and saved as JPG, PNG, GIF files.`);
            setInfoModalVisible(true);
        }
        return validFiles.map((file, index) => {
            return new ItemImageFile(file);
        });
    }

    return (
        <>

            <div className="row g-2 mt-3">
                {viewOnly ? (
                    <>

                        {item && itemImages.sort((a, b) => {
                            if (a.sortOrder < b.sortOrder) return -1;
                            if (a.sortOrder > b.sortOrder) return 1;
                            return 0;
                        }).map((img, index) => (
                            <div key={index}
                                className=" position-relative me-1"
                                style={{ width: "142px" }}
                            >
                                <div className={`img-container ${Styles.imgContainer} rounded-0 border`} >
                                    <img src={
                                        img instanceof ItemImageFile
                                            ? URL.createObjectURL(img.file)
                                            : `${itemImageURL}/${img.fileName}`
                                    }
                                        alt={`img-${index}`}
                                        className={`img-thumbnail ${Styles.imgThumbnail} border-0`}
                                    />
                                </div>

                                {((img instanceof ItemImageFile && img.file.name === item.primaryImage) || (img instanceof ItemImage && item.primaryImage === img.fileName)) && (
                                    <div className={`${Styles.primaryImageLabel}`}><i className="bi bi-stars"></i> Cover Image</div>
                                )}


                            </div>
                        ))}</>
                ) : (
                    <>
                        <label className="form-label d-block">Photos • {itemImages.length} / {maxNoOfImages} - You can add up to {maxNoOfImages} photos.</label>
                        {item && itemImages.sort((a, b) => {
                            if (a.sortOrder < b.sortOrder) return -1;
                            if (a.sortOrder > b.sortOrder) return 1;
                            return 0;
                        }).map((img, index) => (
                            <div key={index} className=" position-relative me-1"
                                draggable={!viewOnly}  // only draggable when not in viewOnly mode
                                onDragStart={() => (dragItemIndex.current = index)}
                                onDragEnter={() => {dragOverItemIndex.current = index;}}
                                onDragOver={(e) => e.preventDefault()} // Necessary to allow drop
                                onDrop={(e) => handleDropImageItem(e)}
                                onDragEnd={() => {
                                    dragItemIndex.current = null;
                                    dragOverItemIndex.current = null;
                                }}
                                onMouseEnter={() => setHoveredIndex(index)} // Set the index when mouse enters
                                onMouseLeave={() => setHoveredIndex(null)} // Reset when mouse leaves
                                style={{ width: "142px" }}>
                                <div className={`img-container ${Styles.imgContainer} rounded-0 border`} >
                                    <img
                                        src={
                                            img instanceof ItemImageFile
                                                ? URL.createObjectURL(img.file)
                                                : `${itemImageURL}/${img.fileName}`
                                        }
                                        alt={`img-${index}`}
                                        className={`img-thumbnail ${Styles.imgThumbnail} border-0`}
                                    />
                                </div>
                                {hoveredIndex === index && ((img instanceof ItemImageFile && img.file.name !== item.primaryImage) || (img instanceof ItemImage && item.primaryImage !== img.fileName)) && (
                                    <button className={`btn btn-dark ${Styles.setPrimaryBtn}`} type="button" onClick={() => handleSetPrimaryImage(img)} >
                                        <i className="bi bi-magic"></i>  Set as Primary
                                    </button>
                                )}
                                {((img instanceof ItemImageFile && img.file.name === item.primaryImage) || (img instanceof ItemImage && item.primaryImage === img.fileName)) && (
                                    <div className={`${Styles.primaryImageLabel}`}><i className="bi bi-stars"></i> Cover Image</div>
                                )}
                                <button
                                    type="button"
                                    className="btn-close position-absolute top-0 end-0 mt-1 me-1 bg-white rounded-circle p-2"
                                    aria-label="Remove"
                                    onClick={() => handleRemoveImage(index)}
                                ></button>

                            </div>
                        ))}

                        <div className={`${itemImages.length === 0 ? "col-12" : "col-4 col-sm-3 col-md-2"} ${newItemErrors && newItemErrors.primaryImage ? 'is-invalid' : ''}`}>
                            <div className={`rounded-0 d-flex flex-column justify-content-center align-items-center text-center p-2 cursor-pointer
                            ${dragOver ? "border border-3 bg-light" : "border border-1 border-secondary"}
                            ${newItemErrors && newItemErrors.primaryImage ? "border-danger" : ""}`}
                                style={{ width: itemImages.length === 0 ? "" : "142px", height: itemImages.length === 0 ? "150px" : "127px" }}
                                onClick={() => fileInputRef.current?.click()}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                <i className="bi bi-file-plus fs-4" ></i>
                                <span className={`${itemImages.length === 0 ? "fs-5" : "fs-6"}`}>
                                    {itemImages.length === 0 ? 'Add Photos' : 'Add Photo'}
                                </span>
                                {itemImages.length === 0 && <span className="fs-6">
                                    or drag and drop
                                </span>}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".jpg, .jpeg, .png, .gif"
                                multiple
                                hidden
                                onChange={handleImageChange}
                            />
                        </div>
                        <label className="form-label d-block fw-lighter">Tip: Drag and Drop the photos to re-arrange order. For best display, we recommend each photo is maximum {maxImageSizeMB}MB in (.jpg, .png, .gif) format and in square shape. </label>

                        {newItemErrors && newItemErrors.primaryImage && <div className="invalid-feedback">{newItemErrors.primaryImage}</div>}
                    </>
                )}


            </div >

            <InfoDialogueComp
                show={infoModalVisible}
                title="Information"
                message={infoModalMessage}
                onClose={() => { setInfoModalMessage(""); setInfoModalVisible(false); }}
            />
        </>
    );

}
export default ItemImageUploadComp;